import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  AddIcon, CloseIcon, DeleteIcon, WarningIcon,
} from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { LuEllipsis } from 'react-icons/lu';
import {
  createAccount,
  createLoan,
  deleteValuation,
  fetchValuations,
  foldHoldingInto,
  recordLoanPayment,
  recordPrices,
} from '../../api/endpointsBackend';
import { CustomDropdown } from '../movements/MovementDetailPanel';
import { formatMoney, formatPhoneBalance } from '../../utils/formatters';
import { formatDate } from '../../utils/intlFormatters';
import { todayLocalISO } from '../../utils/localDate';
import {
  brokerValue, childrenOf, formatPrice, formatUnits, inDisplay, isHolding, isSettledLoan, proposePaymentParts,
} from '../../utils/subAccounts';
import { TONE, toneOf } from './tones';
import DeltaPill from './DeltaPill';
import { holdingReturnNote } from '../../utils/holdingReturns';

// The detail of a sub-account and of its parent, shown in the account panel
// around the balance chart: a holding's position and prices, a broker's value
// (above the chart) and its allocation and holdings (below it), a person's
// loans, a loan's figures. The actions (Update price,
// Add holding, New loan, Record payment…) show only where the panel may edit.

const money = (currency, n) => (n == null ? '—' : formatPhoneBalance(currency, n));
const pct = (n) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}%`);
// A gain reads green; a loss stays in ink, red being kept for something to fix.
const tone = (n) => (n == null || Math.abs(n) < 0.005 ? undefined : n > 0 ? 'redesign.amount.income' : undefined);
const toNumber = (v) => {
  const n = Number(v);
  return v === '' || v == null || !Number.isFinite(n) ? 0 : n;
};
// What a 4xx says, in the server's words, else the fallback.
const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

function useActionToast() {
  const toast = useToast();
  const { t } = useTranslation('accounts');
  return {
    ok: (description) => toast({ title: t('detail.toasts.successTitle'), description, status: 'success', duration: 3000, isClosable: true }),
    fail: (err) => toast({
      title: t('detail.toasts.errorTitle'),
      description: errorText(err, t('sub.toasts.error')),
      status: 'error',
      duration: 5000,
      isClosable: true,
    }),
  };
}

function Section({ title, actions, children }) {
  return (
    <Box mb={6}>
      {/* Copilot's section heading ("Allocation", "Holdings"): a step up, in ink. */}
      <Flex align="center" justify="space-between" gap={2} mb={3} flexWrap="wrap">
        <Text fontSize="20px" fontWeight={600} lineHeight="26px" color="redesign.heading">
          {title}
        </Text>
        {actions && <Flex gap={2} flexWrap="wrap">{actions}</Flex>}
      </Flex>
      {children}
    </Box>
  );
}

function Figure({ label, value, hint, color }) {
  return (
    <Box minW={0}>
      <Text fontSize="2xs" color="redesign.text.muted" isTruncated>{label}</Text>
      <Text fontSize="sm" fontWeight={600} color={color || 'redesign.heading'} whiteSpace="nowrap">{value}</Text>
      {hint && <Text fontSize="2xs" color="redesign.text.muted">{hint}</Text>}
    </Box>
  );
}

function Warning({ children }) {
  return (
    <Flex align="flex-start" gap={2} {...TONE.warn} px={3} py={2} borderRadius="md" mt={2}>
      <WarningIcon color="redesign.accent.amber" boxSize={3.5} mt="2px" flexShrink={0} />
      <Text fontSize="xs" color="redesign.text">{children}</Text>
    </Flex>
  );
}

function ActionButton({ children, ...rest }) {
  return <Button size="xs" variant="outline" {...rest}>{children}</Button>;
}

function Field({ label, children }) {
  return (
    <Box mb={3}>
      <Text fontSize="sm" fontWeight={600} color="redesign.text" mb={1}>{label}</Text>
      {children}
    </Box>
  );
}

// The picker the panel's forms use for accounts and categories, boxed as a field.
function Picker(props) {
  return (
    <Flex px={3} py={1.5} minH="40px" align="center" borderRadius="md" border="1px solid" borderColor="redesign.border">
      <CustomDropdown {...props} />
    </Flex>
  );
}

function FormModal({ isOpen, onClose, title, onSubmit, saving, canSubmit, submitLabel, size = 'md', children }) {
  const { t } = useTranslation('accounts');
  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} isCentered size={size}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{title}</ModalHeader>
        <ModalCloseButton isDisabled={saving} />
        <ModalBody>{children}</ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={saving}>{t('detail.buttons.cancel')}</Button>
          <Button variant="primary" onClick={onSubmit} isLoading={saving} isDisabled={!canSubmit || saving}>
            {submitLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Runs one write: disables the button while it runs, toasts, and reports back. */
function useSubmit(onDone) {
  const [saving, setSaving] = useState(false);
  const { ok, fail } = useActionToast();
  const run = async (write, successText) => {
    setSaving(true);
    try {
      const result = await write();
      ok(successText);
      onDone?.(result);
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };
  return { saving, run };
}

// ── Holding ────────────────────────────────────────────────────────────────

function UpdatePriceModal({ holding, isOpen, onClose, onSaved }) {
  const { t } = useTranslation('accounts');
  const [date, setDate] = useState(todayLocalISO());
  const [price, setPrice] = useState('');
  const { saving, run } = useSubmit(() => { onClose(); onSaved(); });
  useEffect(() => { if (isOpen) { setDate(todayLocalISO()); setPrice(''); } }, [isOpen]);
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('sub.updatePrice')} · ${holding.instrument_symbol || holding.name}`}
      saving={saving}
      canSubmit={Boolean(date) && toNumber(price) > 0}
      submitLabel={t('detail.buttons.save')}
      onSubmit={() => run(
        () => recordPrices(holding.id, date, [{ account_id: holding.id, unit_price: toNumber(price) }]),
        t('sub.toasts.priceSaved'),
      )}
    >
      <Field label={t('sub.date')}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label={`${t('sub.pricePerUnit')} (${holding.position?.currency || holding.currencies?.[0] || ''})`}>
        <Input type="number" step="any" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
          placeholder={holding.position?.price != null ? formatPrice(holding.position.price) : '0.00'} />
      </Field>
    </FormModal>
  );
}

function SameAsModal({ holding, siblings, isOpen, onClose, onMerged }) {
  const { t } = useTranslation('accounts');
  const [targetId, setTargetId] = useState(null);
  const { saving, run } = useSubmit((target) => { onClose(); onMerged(target?.id ?? targetId); });
  useEffect(() => { if (isOpen) setTargetId(null); }, [isOpen]);
  const options = siblings.map((a) => ({
    id: a.id,
    name: a.instrument_symbol && a.instrument_symbol !== a.name ? `${a.instrument_symbol} · ${a.name}` : a.name,
  }));
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sub.sameAsTitle')}
      saving={saving}
      canSubmit={targetId != null}
      submitLabel={t('sub.sameAsConfirm')}
      onSubmit={() => run(() => foldHoldingInto(holding.id, targetId), t('sub.toasts.merged'))}
    >
      <Text fontSize="sm" color="redesign.text.muted" mb={3}>
        {t('sub.sameAsHint', { symbol: holding.instrument_symbol || holding.name })}
      </Text>
      <Field label={t('sub.sameAsPick')}>
        <Picker value={targetId} options={options} onChange={(o) => setTargetId(o ? o.id : null)} />
      </Field>
    </FormModal>
  );
}

/**
 * A holding's headline, beside its chart: what the position is worth at its
 * last price (at cost while it has none), what that gained over its cost, and
 * the units and price it comes from. With `convert` (the page's display
 * currency), the value and gain are in it, marked "≈"; the price stays in its own.
 * `ret` is its return from the holding-returns endpoint (in the display currency,
 * or over the chart's range, `rangeLabel`); without one, its own since purchase.
 * `paid` is its since-purchase row in the display currency, for what it cost.
 */
export function HoldingSummary({
  account, phone = false, convert = null, ret = null, rangeLabel = null, paid = null,
}) {
  const { t } = useTranslation('accounts');
  const p = account.position || {};
  const currency = p.currency || account.currencies?.[0] || '';
  const value = p.market_value ?? p.cost ?? 0;
  const priced = p.price != null;
  const c = convertFrom(convert, currency);
  const shownIn = c ? c.target : currency;
  const approx = c && currency !== c.target ? '≈ ' : '';
  const shownValue = inDisplay(c, currency, value);
  const r = ret?.gain != null ? ret : null;
  return (
    <Box mb={4}>
      <Flex align="center" gap={3} flexWrap="wrap" rowGap={1}>
        <Text fontSize="3xl" fontWeight="bold" color="redesign.heading" lineHeight={1.15}>
          {`${approx}${phone ? formatPhoneBalance(shownIn, shownValue) : formatMoney(shownValue, shownIn)}`}
        </Text>
        {r ? (
          <GainPills
            gain={r.gain}
            text={`${approx}${r.gain >= 0 ? '+' : '−'}${money(shownIn, Math.abs(r.gain))}`}
            pct={r.pct}
            note={holdingReturnNote(r, t)}
            label={rangeLabel || t('sub.sinceBought')}
            title={t('sub.unrealized')}
          />
        ) : p.unrealized_gain != null && (
          <GainPills
            gain={p.unrealized_gain}
            text={`${approx}${p.unrealized_gain >= 0 ? '+' : '−'}${money(shownIn, inDisplay(c, currency, Math.abs(p.unrealized_gain)))}`}
            pct={p.unrealized_pct}
            label={t('sub.sinceBought')}
            title={t('sub.unrealized')}
          />
        )}
      </Flex>
      {paid?.cost != null && paid.own_cost > 0 && approx && (
        <Text fontSize="xs" color="redesign.text.muted" mt={1}>
          {t('sub.paidInDisplay', {
            amount: money(shownIn, paid.cost),
            rate: Math.round(paid.cost / paid.own_cost).toLocaleString('en-US'),
            own: currency,
          })}
        </Text>
      )}
      <Text fontSize="xs" color="redesign.text.muted" mt={1}>
        {[
          t('sub.unitsCount', { units: formatUnits(p.units), count: Number(p.units) || 0 }),
          priced ? `${currency} ${formatPrice(p.price)}` : t('sub.noPrice'),
          priced && p.price_date && formatDate(p.price_date),
        ].filter(Boolean).join(' · ')}
      </Text>
    </Box>
  );
}

// One label and its figure, on a line.
function FigureLine({ label, value, color }) {
  return (
    <Flex justify="space-between" align="baseline" gap={3} py={1.5} borderBottom="1px solid" borderColor="redesign.border">
      <Text fontSize="sm" color="redesign.text.muted">{label}</Text>
      <Text fontSize="sm" fontWeight={600} color={color || 'redesign.heading'} whiteSpace="nowrap">{value}</Text>
    </Flex>
  );
}

/**
 * A holding's detail, below its chart: its cost and what it already earned,
 * and its prices, the latest one first and the rest folded. The headline
 * (value, gain, units) is `HoldingSummary`'s. With `convert`, the amounts are
 * in the page's display currency; the average cost and the prices stay in their own.
 */
export function HoldingDetail({
  account, accounts, canEdit, onChanged, onListChanged, onSelectAccount, convert = null, paid = null,
}) {
  const { t } = useTranslation('accounts');
  const { ok, fail } = useActionToast();
  const p = account.position || {};
  const currency = p.currency || account.currencies?.[0] || '';
  const c = convertFrom(convert, currency);
  const [valuations, setValuations] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState(null);
  const [showAllPrices, setShowAllPrices] = useState(false);
  const siblings = (accounts || []).filter((a) => a.id !== account.id && a.parent_account_id === account.parent_account_id && isHolding(a));

  useEffect(() => {
    let active = true;
    fetchValuations(account.id)
      .then((rows) => { if (active) setValuations([...(rows || [])].sort((a, b) => b.as_of_date.localeCompare(a.as_of_date))); })
      .catch(() => { if (active) setValuations([]); });
    return () => { active = false; };
  }, [account.id, reloadKey]);

  const changed = () => { setReloadKey((k) => k + 1); onChanged(); };
  const removePrice = async (v) => {
    try {
      await deleteValuation(account.id, v.id);
      ok(t('sub.toasts.priceDeleted'));
      changed();
    } catch (err) {
      fail(err);
    }
  };

  const check = p.units_check;
  const shownPrices = valuations && !showAllPrices ? valuations.slice(0, 1) : valuations;
  return (
    <>
      <Section
        title={t('sub.position')}
        actions={canEdit && (
          <>
            <ActionButton onClick={() => setModal('price')}>{t('sub.updatePrice')}</ActionButton>
            {siblings.length > 0 && (
              <Menu placement="bottom-end" isLazy>
                <MenuButton as={IconButton} icon={<LuEllipsis />} size="xs" variant="outline" aria-label={t('sub.moreActions')} />
                <MenuList minW="180px" py={1}>
                  <MenuItem fontSize="sm" onClick={() => setModal('same')}>{t('sub.sameAs')}</MenuItem>
                </MenuList>
              </Menu>
            )}
          </>
        )}
      >
        {/* In the display currency, what a unit cost on average: what was
            paid (each buy at its own day's rate) over the units held, with
            the own-currency average beside it. */}
        <FigureLine
          label={t('sub.averageCost')}
          value={p.average_cost == null ? '—' : paid?.cost != null && Number(p.units) > 0 && c && currency !== c.target ? (
            <>
              {`≈ ${c.target} ${formatPrice(paid.cost / Number(p.units))}`}
              <Text as="span" fontWeight={400} color="redesign.text.muted" ml={2}>{`${currency} ${formatPrice(p.average_cost)}`}</Text>
            </>
          ) : `${currency} ${formatPrice(p.average_cost)}`}
        />
        {/* In the display currency, what was paid: each buy at its own day's
            rate, as the summary's "Paid" line says, not today's. */}
        <FigureLine
          label={t('sub.cost')}
          value={paid?.cost != null && c && currency !== c.target ? `≈ ${money(c.target, paid.cost)}` : shown(c, currency, p.cost)}
        />
        {Math.abs(p.realized_gain || 0) >= 0.005 && (
          <FigureLine label={t('sub.realized')} value={shown(c, currency, p.realized_gain)} color={tone(p.realized_gain)} />
        )}
        {(p.fees || 0) >= 0.005 && <FigureLine label={t('sub.fees')} value={shown(c, currency, p.fees)} />}
        {p.oversold && <Warning>{t('sub.oversold')}</Warning>}
        {check && !check.matches && (
          <Warning>
            {t('sub.unitsCheck', {
              statement: formatUnits(check.statement_units),
              app: formatUnits(check.app_units),
              date: formatDate(check.as_of_date),
            })}
          </Warning>
        )}
      </Section>

      <Section title={t('sub.priceHistory')}>
        {valuations == null ? null : valuations.length === 0 ? (
          <Text fontSize="sm" color="redesign.text.muted">{t('sub.noPrices')}</Text>
        ) : shownPrices.map((v) => (
          <Flex key={v.id} align="center" gap={3} py={1} borderBottom="1px solid" borderColor="redesign.border" fontSize="sm">
            <Text w="110px" flexShrink={0} color="redesign.text.muted">{formatDate(v.as_of_date)}</Text>
            <Text flex={1} fontWeight={500}>{`${v.currency} ${formatPrice(v.unit_price)}`}</Text>
            <Text fontSize="xs" color="redesign.text.muted">{t(`sub.derivation.${v.derivation}`, { defaultValue: v.derivation })}</Text>
            <Box w="24px" flexShrink={0}>
              {canEdit && v.derivation === 'manual' && (
                <IconButton
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  aria-label={t('sub.deletePriceAria', { date: formatDate(v.as_of_date) })}
                  onClick={() => removePrice(v)}
                />
              )}
            </Box>
          </Flex>
        ))}
        {valuations && valuations.length > 1 && (
          <Button size="xs" variant="ghost" mt={1} px={0} color="redesign.text.muted" fontWeight={500}
            _hover={{ color: 'redesign.heading', bg: 'transparent' }} onClick={() => setShowAllPrices((v) => !v)}>
            {showAllPrices ? t('sub.fewerPrices') : t('sub.allPrices', { count: valuations.length })}
          </Button>
        )}
      </Section>

      {canEdit && (
        <>
          <UpdatePriceModal holding={account} isOpen={modal === 'price'} onClose={() => setModal(null)} onSaved={changed} />
          <SameAsModal
            holding={account}
            siblings={siblings}
            isOpen={modal === 'same'}
            onClose={() => setModal(null)}
            onMerged={(targetId) => {
              // This holding is gone: open the one it was folded into.
              if (onSelectAccount && targetId != null) {
                onSelectAccount(targetId);
                onListChanged?.();
              } else {
                onChanged();
              }
            }}
          />
        </>
      )}
    </>
  );
}

// ── Broker ─────────────────────────────────────────────────────────────────

function UpdatePricesModal({ broker, holdings, isOpen, onClose, onSaved }) {
  const { t } = useTranslation('accounts');
  const [date, setDate] = useState(todayLocalISO());
  const [prices, setPrices] = useState({});
  const { saving, run } = useSubmit(() => { onClose(); onSaved(); });
  useEffect(() => { if (isOpen) { setDate(todayLocalISO()); setPrices({}); } }, [isOpen]);
  const typed = holdings
    .filter((h) => toNumber(prices[h.id]) > 0)
    .map((h) => ({ account_id: h.id, unit_price: toNumber(prices[h.id]) }));
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sub.updatePrices')}
      saving={saving}
      canSubmit={Boolean(date) && typed.length > 0}
      submitLabel={t('detail.buttons.save')}
      onSubmit={() => run(() => recordPrices(broker.id, date, typed), t('sub.toasts.pricesSaved'))}
    >
      <Field label={t('sub.date')}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Text fontSize="sm" fontWeight={600} color="redesign.text" mb={1}>{t('sub.pricePerUnit')}</Text>
      {holdings.map((h) => (
        <Flex key={h.id} align="center" gap={3} mb={2}>
          <Text flex={1} fontSize="sm" isTruncated>{h.instrument_symbol || h.name}</Text>
          <Text fontSize="xs" color="redesign.text.muted">{h.position?.currency || h.currencies?.[0]}</Text>
          <Input
            type="number"
            step="any"
            min="0"
            size="sm"
            w="140px"
            value={prices[h.id] ?? ''}
            onChange={(e) => setPrices((prev) => ({ ...prev, [h.id]: e.target.value }))}
            placeholder={h.position?.price != null ? formatPrice(h.position.price) : '0.00'}
            aria-label={`${t('sub.pricePerUnit')} · ${h.instrument_symbol || h.name}`}
          />
        </Flex>
      ))}
    </FormModal>
  );
}

function AddHoldingModal({ broker, isOpen, onClose, onSaved }) {
  const { t } = useTranslation('accounts');
  const currencies = broker.currencies || [];
  const [symbol, setSymbol] = useState('');
  const [currency, setCurrency] = useState(currencies[0] || '');
  const [type, setType] = useState('stock');
  const { saving, run } = useSubmit(() => { onClose(); onSaved(); });
  useEffect(() => {
    if (isOpen) { setSymbol(''); setCurrency((broker.currencies || [])[0] || ''); setType('stock'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const clean = symbol.trim().toUpperCase();
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('sub.addHolding')}
      saving={saving}
      canSubmit={Boolean(clean && currency)}
      submitLabel={t('sub.addHolding')}
      onSubmit={() => run(() => createAccount({
        name: clean,
        account_type: type,
        parent_account_id: broker.id,
        instrument_symbol: clean,
        currencies: [currency],
      }), t('sub.toasts.holdingAdded'))}
    >
      <Field label={t('sub.symbol')}>
        <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="SPY" />
      </Field>
      <Field label={t('sub.currency')}>
        <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
          {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label={t('sub.type')}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="stock">{t('types.stock')}</option>
          <option value="funds">{t('types.funds')}</option>
        </Select>
      </Field>
    </FormModal>
  );
}

const isClosed = (h) => Math.abs(Number(h.position?.units) || 0) < 1e-9;
// An amount as the panel shows it: "≈" when it was converted to the page's
// display currency from another one.
const shown = (convert, currency, n) => {
  if (n == null) return '—';
  const text = money(convert ? convert.target : currency, inDisplay(convert, currency, n));
  return convert && currency !== convert.target ? `≈ ${text}` : text;
};
// The page's display currency for a holding's figures, while its currency has
// a rate to it; without one, they stay in their own.
const convertFrom = (convert, currency) => (inDisplay(convert, currency, 1) == null ? null : convert);
// A holding's value, and its currency: the market value, at cost while it has no price.
const holdingValue = (h) => {
  const p = h.position || {};
  return { currency: p.currency || h.currencies?.[0], value: p.market_value ?? p.cost };
};

// A gain is good news when it is above zero, and none when it rounds to zero.
const gainIsGood = (value) => (value == null || Math.abs(value) < 0.005 ? null : value > 0);

// A gain in money and as a %, and what it covers ("since bought", "over 1Y").
// `note` (the return in the holding's own currency and the exchange rate's
// share of the gap) shows on the % pill.
function GainPills({ gain, text, pct, note = null, label, title }) {
  return (
    <Flex align="center" gap={1.5} title={title}>
      <Pill value={gain}>{text}</Pill>
      {pct != null && (
        <Tooltip label={note} isDisabled={!note} hasArrow openDelay={300}>
          <Box as="span" display="inline-flex">
            <DeltaPill pct={pct} good={gainIsGood(pct)} up={pct > 0} />
          </Box>
        </Tooltip>
      )}
      {label && <Text fontSize="2xs" color="redesign.text.muted">{label}</Text>}
    </Flex>
  );
}

// A holding's value on its row, at the Accounts rows' size.
function HoldingFigure({ children }) {
  return (
    <Text fontSize="15px" fontWeight={600} color="redesign.heading" textAlign="right" whiteSpace="nowrap" flexShrink={0}>
      {children}
    </Text>
  );
}

/**
 * Copilot's header for a holding or a broker, in parts the detail panel lays
 * out as it does every account's: `pills` on the account's line, `figure` (the
 * value, its code and whether it was converted) beside the name, and `lines`
 * under the name. The pills' tooltip says what the return covers ("since
 * bought", "over 1Y") and, when converted, the own-currency return.
 */
function headlinePills({ gain, text, pct, label, note, t }) {
  if (gain == null) return null;
  return (
    <GainPills
      gain={gain}
      text={text}
      pct={pct}
      note={[label, note].filter(Boolean).join(' · ')}
      title={t('sub.unrealized')}
    />
  );
}

export function holdingHeadline({ account, convert = null, ret = null, rangeLabel = null, paid = null, t }) {
  const p = account.position || {};
  const currency = p.currency || account.currencies?.[0] || '';
  const value = p.market_value ?? p.cost ?? 0;
  const c = convertFrom(convert, currency);
  const shownIn = c ? c.target : currency;
  const approx = Boolean(c && currency !== c.target);
  const r = ret?.gain != null ? ret : null;
  const gain = r ? r.gain : p.unrealized_gain;
  const shownGain = r ? gain : gain != null ? inDisplay(c, currency, gain) : null;
  return {
    figure: { code: shownIn, amount: formatMoney(inDisplay(c, currency, value), shownIn, true), approx },
    pills: headlinePills({
      gain,
      text: shownGain == null ? null : `${approx ? '≈ ' : ''}${gain >= 0 ? '+' : '−'}${money(shownIn, Math.abs(shownGain))}`,
      pct: r ? r.pct : p.unrealized_pct,
      label: r ? rangeLabel || t('sub.sinceBought') : t('sub.sinceBought'),
      note: holdingReturnNote(r, t),
      t,
    }),
    lines: [
      paid?.cost != null && paid.own_cost > 0 && approx && t('sub.paidInDisplay', {
        amount: money(shownIn, paid.cost),
        rate: Math.round(paid.cost / paid.own_cost).toLocaleString('en-US'),
        own: currency,
      }),
      [
        t('sub.unitsCount', { units: formatUnits(p.units), count: Number(p.units) || 0 }),
        p.price != null ? `${currency} ${formatPrice(p.price)}` : t('sub.noPrice'),
        p.price != null && p.price_date && formatDate(p.price_date),
      ].filter(Boolean).join(' · '),
    ].filter(Boolean),
  };
}

// Null when the broker holds more than one currency and none is picked to
// show them in: its summary blocks, one per currency, say it then.
export function brokerHeadline({
  account, accounts, cash, convert = null, ret = null, retCurrency = null, rangeLabel = null, t,
}) {
  const values = brokerValue(cash, childrenOf(accounts, account.id).filter(isHolding), convert);
  if (values.length !== 1) return null;
  const v = values[0];
  const r = ret?.gain != null && v.currency === retCurrency ? ret : null;
  const gain = r ? r.gain : v.gain;
  return {
    figure: { code: v.currency, amount: formatMoney(v.total, v.currency, true), approx: v.isConverted },
    pills: headlinePills({
      gain,
      text: gain == null ? null : `${v.holdingsIsConverted ? '≈ ' : ''}${gain >= 0 ? '+' : '−'}${money(v.currency, Math.abs(gain))}`,
      pct: r ? r.pct : v.gainPct,
      label: r ? rangeLabel || t('sub.sinceBought') : t('sub.sinceBought'),
      note: holdingReturnNote(r, t),
      t,
    }),
    lines: [[
      t('sub.holdingsAmount', { amount: `${v.holdingsIsConverted ? '≈ ' : ''}${money(v.currency, v.holdingsValue)}` }),
      t('sub.cashAmount', { amount: `${v.cashIsConverted ? '≈ ' : ''}${money(v.currency, v.cash)}` }),
      v.priceDate && t('sub.pricesAsOf', { date: formatDate(v.priceDate) }),
    ].filter(Boolean).join(' · ')],
  };
}

// A gain in money, toned by its sign, in the 22px pill of the page's other
// changes. A percentage is a DeltaPill, whose arrow gives the direction.
function Pill({ value, children }) {
  return (
    <Flex
      align="center"
      h="22px"
      px="8px"
      {...TONE[toneOf(gainIsGood(value))]}
      borderRadius="full"
      fontSize="13px"
      fontWeight="600"
      lineHeight={1}
      whiteSpace="nowrap"
    >
      {children}
    </Flex>
  );
}

/**
 * A broker's headline, above its chart: what it holds today (cash plus the
 * holdings at their last price) and what the priced holdings gained over
 * their cost. One block per currency the broker holds; with `convert` (the
 * page's display currency), one block in it, marked "≈". `ret` is the broker's
 * return in `retCurrency` from the holding-returns endpoint (each buy at its
 * own day's rate, or over the chart's range, `rangeLabel`); the block in that
 * currency reads it in place of its own.
 */
export function BrokerSummary({
  account, accounts, cash, phone = false, convert = null, ret = null, retCurrency = null, rangeLabel = null,
}) {
  const { t } = useTranslation('accounts');
  const values = useMemo(
    () => brokerValue(cash, childrenOf(accounts, account.id).filter(isHolding), convert),
    [cash, accounts, account.id, convert],
  );
  if (values.length === 0) return null;
  const approx = (v) => (v.isConverted ? '≈ ' : '');
  const big = (v, n) => `${approx(v)}${phone ? formatPhoneBalance(v.currency, n) : formatMoney(n, v.currency)}`;
  return (
    <Box mb={4}>
      {values.map((v, i) => (
        <Box key={v.currency} mt={i ? 4 : 0}>
          <Text textStyle="microLabel">
            {t('sub.totalValue')}
          </Text>
          <Flex align="center" gap={3} flexWrap="wrap" rowGap={1}>
            <Text fontSize={i ? '2xl' : '3xl'} fontWeight="bold" color="redesign.heading" lineHeight={1.15}>
              {big(v, v.total)}
            </Text>
            {(() => {
              const r = ret?.gain != null && v.currency === retCurrency ? ret : null;
              const gain = r ? r.gain : v.gain;
              if (gain == null) return null;
              return (
                <GainPills
                  gain={gain}
                  text={`${v.holdingsIsConverted ? '≈ ' : ''}${gain >= 0 ? '+' : '−'}${money(v.currency, Math.abs(gain))}`}
                  pct={r ? r.pct : v.gainPct}
                  note={holdingReturnNote(r, t)}
                  label={r ? rangeLabel || t('sub.sinceBought') : t('sub.sinceBought')}
                  title={t('sub.unrealized')}
                />
              );
            })()}
          </Flex>
          <Text fontSize="xs" color="redesign.text.muted" mt={1}>
            {[
              t('sub.holdingsAmount', { amount: `${v.holdingsIsConverted ? '≈ ' : ''}${money(v.currency, v.holdingsValue)}` }),
              t('sub.cashAmount', { amount: `${v.cashIsConverted ? '≈ ' : ''}${money(v.currency, v.cash)}` }),
              v.priceDate && t('sub.pricesAsOf', { date: formatDate(v.priceDate) }),
            ].filter(Boolean).join(' · ')}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

// One line of a broker's allocation, as Copilot draws it: the label, a thin
// grey bar of its share, and the share.
function AllocationRow({ label, pct }) {
  return (
    <Flex align="center" gap={4} minH="33px">
      <Text fontSize="15px" color="redesign.heading" w="110px" flexShrink={0} isTruncated>{label}</Text>
      <Box flex={1} h="5px" borderRadius="full" bg="redesign.bg.card" overflow="hidden">
        <Box h="100%" w={`${Math.max(pct, 0.5)}%`} bg="redesign.text.muted" borderRadius="full" />
      </Box>
      <Text fontSize="15px" fontWeight={600} color="redesign.heading" w="64px" textAlign="right" flexShrink={0}>
        {`${pct.toFixed(1)}%`}
      </Text>
    </Flex>
  );
}

// One holding, as Copilot lists them: the ticker and the security's name,
// then its return and its value; its type and its units and price under, the
// type outlined as Copilot draws it, the rest in italic as the Accounts rows
// carry their second line. The panel is narrower than Copilot's, so the type
// leaves the first line to the name.
function HoldingRow({ symbol, name, tag, detail, pill, figure, dim = false, onClick }) {
  return (
    <Flex
      align="center"
      gap={3}
      minH="50px"
      py="7px"
      px={2}
      mx={-2}
      borderRadius="md"
      opacity={dim ? 0.6 : 1}
      cursor={onClick ? 'pointer' : undefined}
      _hover={onClick ? { bg: 'redesign.bg.hover' } : undefined}
      onClick={onClick}
    >
      <Box flex={1} minW={0}>
        <Flex align="center" gap="10px" minW={0}>
          <Text fontSize="15px" fontWeight={600} color={name ? 'redesign.text.muted' : 'redesign.heading'} flexShrink={0}>{symbol}</Text>
          {name && <Text fontSize="15px" color="redesign.heading" isTruncated>{name}</Text>}
        </Flex>
        {(tag || detail) && (
          <Flex align="center" gap="6px" minW={0} mt="2px">
            {tag && (
              <Box
                as="span"
                flexShrink={0}
                px="5px"
                h="18px"
                lineHeight="15px"
                border="1.5px solid"
                borderColor="redesign.border"
                borderRadius="5px"
                fontSize="11px"
                color="redesign.text.muted"
              >
                {tag}
              </Box>
            )}
            {detail && <Text fontSize="13px" fontStyle="italic" color="redesign.text.muted" lineHeight="18px" isTruncated>{detail}</Text>}
          </Flex>
        )}
      </Box>
      {pill}
      {figure}
    </Flex>
  );
}

/**
 * A broker's positions, below its chart: one row per holding (open ones by
 * value, sold ones last), each with its share of what the broker holds and
 * opening the holding, and the cash when there is some. With `convert`, the
 * values are in the page's display currency; a price stays in its own.
 */
export function BrokerDetail({ account, accounts, cash, canEdit, onChanged, onSelectAccount, convert = null, returns = null }) {
  const { t } = useTranslation('accounts');
  const [modal, setModal] = useState(null);
  const holdings = useMemo(() => childrenOf(accounts, account.id).filter(isHolding), [accounts, account.id]);
  const values = useMemo(() => brokerValue(cash, holdings, convert), [cash, holdings, convert]);
  const sortValue = (h) => {
    const { currency, value } = holdingValue(h);
    return inDisplay(convert, currency, value) ?? 0;
  };
  const rows = [...holdings].sort((a, b) => Number(isClosed(a)) - Number(isClosed(b)) || sortValue(b) - sortValue(a));
  const cashLines = values.filter((v) => v.cashShare != null);
  const allocation = values.flatMap((v) => v.allocation.map((x) => ({ ...x, currency: v.currency })));
  const partLabel = (x) => (x.key === 'cash' ? t('sub.cash') : x.key === 'other' ? t('sub.others') : x.label);
  return (
    <>
      {/* Copilot's brokerage view: where the money sits, then each holding. */}
      {allocation.length > 0 && (
        <Section title={t('sub.allocation')}>
          {allocation.map((x) => (
            <AllocationRow key={`${x.currency}-${x.key}`} label={partLabel(x)} pct={x.pct} />
          ))}
        </Section>
      )}
      <Box h="1px" bg="redesign.border" mb={6} />
      <Section
        title={t('sub.holdings')}
        actions={canEdit && (
          <>
            {holdings.length > 0 && <ActionButton onClick={() => setModal('prices')}>{t('sub.updatePrices')}</ActionButton>}
            <ActionButton leftIcon={<AddIcon boxSize={2} />} onClick={() => setModal('add')}>{t('sub.addHolding')}</ActionButton>
          </>
        )}
      >
        {holdings.length === 0 ? (
          <Text fontSize="sm" color="redesign.text.muted">{t('sub.noHoldings')}</Text>
        ) : (
          <Box>
            {rows.map((h) => {
              const p = h.position || {};
              const { currency, value } = holdingValue(h);
              const closed = isClosed(h);
              // The holding's return from the endpoint when the panel asked
              // for one (display currency or range), else its own since
              // purchase; never one in place of the other.
              const r = returns?.holdings.get(h.id);
              const pct = closed ? null : returns ? r?.pct ?? null : p.unrealized_pct;
              return (
                <HoldingRow
                  key={h.id}
                  symbol={h.instrument_symbol || h.name}
                  name={(h.note || '').split('\n')[0]}
                  tag={t(`types.${h.account_type}`, { defaultValue: h.account_type })}
                  detail={closed ? t('sub.sold') : [
                    t('sub.unitsCount', { units: formatUnits(p.units), count: Number(p.units) || 0 }),
                    p.price != null ? `${currency || ''} ${formatPrice(p.price)}` : t('sub.noPrice'),
                  ].join(' · ')}
                  pill={pct != null && (
                    <Tooltip label={holdingReturnNote(r, t)} isDisabled={!holdingReturnNote(r, t)} hasArrow openDelay={300}>
                      <Box as="span" display="inline-flex">
                        <DeltaPill pct={pct} good={gainIsGood(pct)} up={pct > 0} />
                      </Box>
                    </Tooltip>
                  )}
                  figure={<HoldingFigure>{shown(convert, currency, value)}</HoldingFigure>}
                  dim={closed}
                  onClick={onSelectAccount ? () => onSelectAccount(h.id) : undefined}
                />
              );
            })}
            {cashLines.map((v) => (
              <HoldingRow
                key={`cash-${v.currency}`}
                symbol={t('sub.cash')}
                figure={<HoldingFigure>{`${v.cashIsConverted ? '≈ ' : ''}${money(v.currency, v.cash)}`}</HoldingFigure>}
              />
            ))}
          </Box>
        )}
        {canEdit && (
          <>
            <UpdatePricesModal broker={account} holdings={holdings} isOpen={modal === 'prices'} onClose={() => setModal(null)} onSaved={onChanged} />
            <AddHoldingModal broker={account} isOpen={modal === 'add'} onClose={() => setModal(null)} onSaved={onChanged} />
          </>
        )}
      </Section>
    </>
  );
}

// ── Person and loans ───────────────────────────────────────────────────────

// The accounts money comes from or goes to: the user's own, not people.
const ownAccounts = (accounts) => (accounts || []).filter((a) => a.account_type !== 'lend' && a.is_active !== false);
const loanCurrency = (loan) => loan.loan?.currency || loan.currencies?.[0];

function NewLoanModal({ person, accounts, isOpen, onClose, onSaved }) {
  const { t } = useTranslation('accounts');
  const [date, setDate] = useState(todayLocalISO());
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [amount, setAmount] = useState('');
  const [fromId, setFromId] = useState(null);
  const [currency, setCurrency] = useState('');
  const [note, setNote] = useState('');
  const { saving, run } = useSubmit(() => { onClose(); onSaved(); });
  const from = (accounts || []).find((a) => a.id === fromId);
  // The money leaves in a currency the account it leaves from holds.
  const currencies = from?.currencies?.length ? from.currencies : (person.currencies || []);
  useEffect(() => {
    if (!isOpen) return;
    setDate(todayLocalISO());
    setNameTouched(false);
    setAmount('');
    setFromId(null);
    setCurrency((person.currencies || [])[0] || '');
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  // The name follows the date until it is typed.
  useEffect(() => {
    if (!nameTouched) setName(t('sub.loanDefaultName', { date: formatDate(date) }));
  }, [date, nameTouched, t]);
  const currencyKey = currencies.join();
  useEffect(() => {
    if (!currencies.includes(currency)) setCurrency(currencies[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyKey, currency]);
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('sub.newLoan')} · ${person.name}`}
      saving={saving}
      canSubmit={Boolean(name.trim() && date && toNumber(amount) > 0 && fromId && currency)}
      submitLabel={t('sub.newLoan')}
      onSubmit={() => run(() => createLoan(person.id, {
        name: name.trim(),
        date,
        amount: toNumber(amount),
        currency,
        from_account_id: fromId,
        ...(note.trim() ? { note: note.trim() } : {}),
      }), t('sub.toasts.loanCreated'))}
    >
      <Field label={t('sub.date')}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label={t('sub.loanName')}>
        <Input value={name} onChange={(e) => { setNameTouched(true); setName(e.target.value); }} />
      </Field>
      <Flex gap={3}>
        <Box flex={1}>
          <Field label={t('sub.amount')}>
            <Input type="number" step="any" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          </Field>
        </Box>
        <Box w="110px">
          <Field label={t('sub.currency')}>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </Box>
      </Flex>
      <Field label={t('sub.fromAccount')}>
        <Picker value={fromId} options={ownAccounts(accounts)} onChange={(o) => setFromId(o ? o.id : null)} showInactiveIndicator />
      </Field>
      <Field label={t('sub.note')}>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('detail.fields.notePlaceholder')} />
      </Field>
    </FormModal>
  );
}

const INTEREST_CATEGORY = /interest|inter[eé]s|investment returns|rendimiento/i;

function RecordPaymentModal({ person, loans, accounts, categories, isOpen, onClose, onSaved }) {
  const { t } = useTranslation('accounts');
  const incomeCategories = useMemo(() => (categories || []).filter((c) => c.type === 'Income'), [categories]);
  const [date, setDate] = useState(todayLocalISO());
  const [received, setReceived] = useState('');
  const [currency, setCurrency] = useState('');
  const [toId, setToId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [note, setNote] = useState('');
  const [parts, setParts] = useState([]);
  const { saving, run } = useSubmit(() => { onClose(); onSaved(); });

  const currencies = [...new Set([...(person.currencies || []), ...loans.map(loanCurrency).filter(Boolean)])];
  const openLoans = loans.filter((l) => !isSettledLoan(l) && loanCurrency(l) === currency);

  useEffect(() => {
    if (!isOpen) return;
    setDate(todayLocalISO());
    setReceived('');
    setCurrency((person.currencies || [])[0] || loanCurrency(loans[0] || {}) || '');
    setToId(null);
    setCategoryId(incomeCategories.find((c) => INTEREST_CATEGORY.test(c.name))?.id ?? null);
    setNote('');
    setParts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // A new amount or currency proposes the split again; the parts stay editable.
  useEffect(() => {
    if (!isOpen) return;
    setParts(proposePaymentParts(toNumber(received), openLoans, person.id)
      .map((p) => ({ account_id: p.account_id, principal: String(p.principal), interest: p.interest ? String(p.interest) : '' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [received, currency, isOpen]);

  const partOptions = [
    { id: person.id, name: t('sub.ownBalance', { name: person.name }) },
    ...openLoans.map((l) => ({ id: l.id, name: t('sub.loanOwes', { name: l.name, amount: money(currency, l.loan?.owed) }) })),
  ];
  const numbers = parts.map((p) => ({ account_id: p.account_id, principal: toNumber(p.principal), interest: toNumber(p.interest) }));
  const filled = numbers.filter((p) => p.principal > 0 || p.interest > 0);
  const total = filled.reduce((s, p) => s + p.principal + p.interest, 0);
  const hasInterest = filled.some((p) => p.interest > 0);

  // What stops the payment from being recorded, in words; a part with no
  // account picked yet only keeps the button off.
  let problem = null;
  const ids = filled.map((p) => p.account_id).filter((id) => id != null);
  if (new Set(ids).size !== ids.length) problem = t('sub.errors.duplicate');
  else if (filled.some((p) => p.account_id === person.id && p.interest > 0)) problem = t('sub.errors.interestOnPerson');
  else {
    const over = filled
      .map((p) => ({ p, loan: openLoans.find((l) => l.id === p.account_id) }))
      .find(({ p, loan }) => loan && p.principal > (loan.loan?.owed || 0) + 0.005);
    if (over) problem = t('sub.errors.overpay', { name: over.loan.name, owed: money(currency, over.loan.loan?.owed) });
    else if (toNumber(received) > 0 && Math.abs(total - toNumber(received)) > 0.005) {
      problem = t('sub.errors.sumMismatch', { parts: money(currency, total), received: money(currency, toNumber(received)) });
    } else if (hasInterest && categoryId == null) problem = t('sub.errors.needsCategory');
  }
  const canSubmit = Boolean(date && currency && toId && filled.length && total > 0 && !problem
    && filled.every((p) => p.account_id != null));

  const setPart = (i, key, value) => setParts((prev) => prev.map((p, j) => (j === i ? { ...p, [key]: value } : p)));

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`${t('sub.recordPayment')} · ${person.name}`}
      saving={saving}
      canSubmit={canSubmit}
      submitLabel={t('sub.recordPayment')}
      onSubmit={() => run(() => recordLoanPayment(person.id, {
        date,
        to_account_id: toId,
        currency,
        ...(hasInterest ? { interest_category_id: categoryId } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        parts: filled,
      }), t('sub.toasts.paymentRecorded'))}
    >
      <Flex gap={3}>
        <Box flex={1}>
          <Field label={t('sub.date')}>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </Box>
        <Box flex={1}>
          <Field label={t('sub.amountReceived')}>
            <Input type="number" step="any" min="0" value={received} onChange={(e) => setReceived(e.target.value)} placeholder="0" />
          </Field>
        </Box>
        <Box w="100px">
          <Field label={t('sub.currency')}>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </Box>
      </Flex>
      <Field label={t('sub.toAccount')}>
        <Picker value={toId} options={ownAccounts(accounts)} onChange={(o) => setToId(o ? o.id : null)} showInactiveIndicator />
      </Field>

      <Text fontSize="sm" fontWeight={600} color="redesign.text" mb={1}>{t('sub.parts')}</Text>
      <Text fontSize="xs" color="redesign.text.muted" mb={2}>{t('sub.partsHint')}</Text>
      {parts.length > 0 && (
        <Flex gap={2} mb={1} fontSize="2xs" color="redesign.text.muted">
          <Text flex={1}>{t('sub.partAccount')}</Text>
          <Text w="110px">{t('sub.principal')}</Text>
          <Text w="110px">{t('sub.interest')}</Text>
          <Box w="24px" />
        </Flex>
      )}
      {parts.map((p, i) => (
        <Flex key={i} gap={2} mb={2} align="center">
          <Box flex={1} minW={0}>
            <Picker value={p.account_id} options={partOptions} onChange={(o) => setPart(i, 'account_id', o ? o.id : null)} />
          </Box>
          <Input type="number" step="any" min="0" size="sm" w="110px" value={p.principal}
            onChange={(e) => setPart(i, 'principal', e.target.value)} aria-label={t('sub.principal')} />
          <Input type="number" step="any" min="0" size="sm" w="110px" value={p.interest}
            onChange={(e) => setPart(i, 'interest', e.target.value)} aria-label={t('sub.interest')} placeholder="0"
            isDisabled={p.account_id === person.id} />
          <IconButton icon={<CloseIcon boxSize={2} />} size="xs" variant="ghost" aria-label={t('sub.removePartAria')}
            onClick={() => setParts((prev) => prev.filter((_, j) => j !== i))} />
        </Flex>
      ))}
      <Button size="xs" variant="ghost" leftIcon={<AddIcon boxSize={2} />} mb={3}
        onClick={() => setParts((prev) => [...prev, { account_id: null, principal: '', interest: '' }])}>
        {t('sub.addPart')}
      </Button>

      {hasInterest && (
        <Field label={t('sub.interestCategory')}>
          <Picker value={categoryId} options={incomeCategories} onChange={(o) => setCategoryId(o ? o.id : null)} />
        </Field>
      )}
      <Field label={t('sub.note')}>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('detail.fields.notePlaceholder')} />
      </Field>
      {problem && <Text fontSize="xs" color="redesign.accent.red">{problem}</Text>}
    </FormModal>
  );
}

function LoanLine({ loan, onSelectAccount }) {
  const { t } = useTranslation('accounts');
  const l = loan.loan || {};
  const cur = loanCurrency(loan);
  return (
    <Flex
      align="center"
      gap={3}
      py={2}
      borderBottom="1px solid"
      borderColor="redesign.border"
      cursor={onSelectAccount ? 'pointer' : undefined}
      _hover={onSelectAccount ? { bg: 'redesign.bg.hover' } : undefined}
      onClick={onSelectAccount ? () => onSelectAccount(loan.id) : undefined}
    >
      <Box flex={1} minW={0}>
        <Text fontSize="sm" fontWeight={500} isTruncated>{loan.name}</Text>
        <Text fontSize="2xs" color="redesign.text.muted" isTruncated>
          {[l.first_date && formatDate(l.first_date),
            t('row.loanLentReceived', { lent: money(cur, l.lent), received: money(cur, l.received) }),
            l.status === 'settled' && l.settled_on && t('sub.settledOn', { date: formatDate(l.settled_on) }),
          ].filter(Boolean).join(' · ')}
        </Text>
      </Box>
      <Box textAlign="right" flexShrink={0}>
        <Text fontSize="sm" fontWeight={600}>{money(cur, l.owed)}</Text>
        {l.annual_effective_pct != null && (
          <Text fontSize="2xs" color={tone(l.annual_effective_pct)}>{`${pct(l.annual_effective_pct)} ${t('balanceChart.annualEffective')}`}</Text>
        )}
      </Box>
    </Flex>
  );
}

export function PersonDetail({ account, accounts, categories, canEdit, onChanged, onSelectAccount }) {
  const { t } = useTranslation('accounts');
  const [modal, setModal] = useState(null);
  const loans = childrenOf(accounts, account.id);
  const open = loans.filter((l) => !isSettledLoan(l));
  const settled = loans.filter(isSettledLoan);
  return (
    <>
      <Section
        title={t('sub.loans')}
        actions={canEdit && (
          <>
            <ActionButton leftIcon={<AddIcon boxSize={2} />} onClick={() => setModal('loan')}>{t('sub.newLoan')}</ActionButton>
            <ActionButton onClick={() => setModal('payment')}>{t('sub.recordPayment')}</ActionButton>
          </>
        )}
      >
        {(account.loans_summary || []).map((s) => (
          <SimpleGrid key={s.currency} columns={{ base: 2, md: 4 }} spacingX={4} spacingY={3} mb={3}>
            <Figure label={t('sub.owesNow')} value={money(s.currency, s.owes_now)} />
            <Figure label={t('sub.lent')} value={money(s.currency, s.lent)} />
            <Figure label={t('sub.interestEarned')} value={money(s.currency, s.interest)} color={tone(s.interest)} />
            <Figure label={t('sub.openLoans')} value={String(s.open_loans)} />
          </SimpleGrid>
        ))}
        {loans.length === 0 ? (
          <Text fontSize="sm" color="redesign.text.muted">{t('sub.noLoans')}</Text>
        ) : (
          <>
            {open.map((l) => <LoanLine key={l.id} loan={l} onSelectAccount={onSelectAccount} />)}
            {settled.length > 0 && (
              <>
                <Text textStyle="microLabel" mt={3} mb={1}>
                  {t('groupSection.settledLoans', { count: settled.length })}
                </Text>
                {settled.map((l) => <LoanLine key={l.id} loan={l} onSelectAccount={onSelectAccount} />)}
              </>
            )}
          </>
        )}
      </Section>
      {canEdit && (
        <>
          <NewLoanModal person={account} accounts={accounts} isOpen={modal === 'loan'} onClose={() => setModal(null)} onSaved={onChanged} />
          <RecordPaymentModal
            person={account}
            loans={loans}
            accounts={accounts}
            categories={categories}
            isOpen={modal === 'payment'}
            onClose={() => setModal(null)}
            onSaved={onChanged}
          />
        </>
      )}
    </>
  );
}

export function LoanDetail({ account }) {
  const { t } = useTranslation('accounts');
  const l = account.loan || {};
  const cur = loanCurrency(account);
  return (
    <Section title={t('sub.loan')}>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={4} spacingY={3}>
        <Figure label={t('sub.lent')} value={money(cur, l.lent)} />
        <Figure label={t('sub.interest')} value={money(cur, l.interest)} color={tone(l.interest)} />
        <Figure label={t('sub.received')} value={money(cur, l.received)} />
        <Figure label={t('sub.owed')} value={money(cur, l.owed)} />
        <Figure
          label={t('sub.status')}
          value={l.status === 'settled' ? t('sub.settled') : t('sub.open')}
          hint={l.status === 'settled' && l.settled_on ? t('sub.settledOn', { date: formatDate(l.settled_on) }) : (l.first_date ? t('sub.since', { date: formatDate(l.first_date) }) : null)}
        />
        <Figure label={t('sub.days')} value={l.days != null ? String(l.days) : '—'} />
        <Figure label={t('sub.annualEffective')} value={pct(l.annual_effective_pct)} color={tone(l.annual_effective_pct)} />
      </SimpleGrid>
    </Section>
  );
}
